/**
 * Unified mapping resolver used by BOTH the canvas editor live preview ("Edit As")
 * and the export engine. Keeps the two surfaces byte-for-byte consistent so a
 * designer-visible color change reliably renders in single-brand export and bulk
 * export across many schools.
 *
 * Design rules:
 *   - Only touch the target property described by the mapping. Other element
 *     properties (position, rotation, font size, etc.) are left untouched so
 *     mapping a color never accidentally mutates text content and vice-versa.
 *   - Support two lookup formats:
 *       a) Flat brand-record map (Record<column, value>) used by the export
 *          engine, which has already merged `ad_school_brands` + `sm_schools`
 *          into a single record.
 *       b) Namespaced `{source}.{table}.{column}` map used by the editor's
 *          `editAsBrandData` state, which can hold many tables at once.
 *   - Fall back to the mapping's stored `fallbackValue` when the data is missing.
 */

import { applyTransform } from '../stores/mappingStore';
import type { EnhancedFieldMapping } from '../types/mapping';
import { normalizeCssColor, isColorProperty } from './mappingValueApplicator';

/**
 * EAV mapping discriminator. A mapping is EAV when it carries both a filter
 * value (the attribute_key) and a filter category. filterColumn is metadata
 * (always 'attribute_key' today) and does not participate in the check.
 */
export function isEavMapping(mapping: EnhancedFieldMapping): boolean {
  return !!(mapping.filterValue && mapping.filterCategory);
}

/**
 * Canonical key for looking up an EAV attribute value inside the export
 * engine's per-table pivot record (sourceDataCache[tableName]). The '@'
 * prefix guarantees pivot keys cannot collide with real column names (a
 * Postgres identifier cannot start with '@').
 */
export function buildEavPivotKey(category: string, attributeKey: string): string {
  return `@${category}.${attributeKey}`;
}

/**
 * Canonical key for looking up an EAV attribute value inside the editor's
 * flat namespaced editAsBrandData lookup (Record<string, unknown> keyed by
 * `{source}.{tableName}.{col-or-pivot-key}`).
 */
export function buildEavBrandDataKey(
  source: string,
  tableName: string,
  category: string,
  attributeKey: string
): string {
  return `${source}.${tableName}.${buildEavPivotKey(category, attributeKey)}`;
}

/**
 * Resolve a mapping against the editor's namespaced `editAsBrandData` lookup.
 * Returns `null` when there is no value and no fallback, so callers can fall
 * back to the element's stored static value.
 */
export function resolveMappedValueFromBrandData(
  mapping: EnhancedFieldMapping,
  brandData: Record<string, unknown> | null
): string | null {
  if (!brandData) return null;

  const lookupKey = isEavMapping(mapping)
    ? buildEavBrandDataKey(
        mapping.source,
        mapping.tableName,
        mapping.filterCategory!,
        mapping.filterValue!
      )
    : `${mapping.source}.${mapping.tableName}.${mapping.columnName}`;
  const value = brandData[lookupKey];

  if (value === undefined || value === null || value === '') {
    return mapping.fallbackValue && mapping.fallbackValue.length > 0
      ? mapping.fallbackValue
      : null;
  }

  let stringValue = String(value);
  if (mapping.transformExpression) {
    stringValue = applyTransform(stringValue, mapping.transformExpression);
  }

  // Silent-fallback rule: if this mapping targets a color property and the
  // resolved value doesn't parse as a valid CSS color, return null so the
  // caller (editor live preview) falls back to the element's static color.
  if (isColorProperty(mapping.targetProperty)) {
    const normalized = normalizeCssColor(stringValue);
    if (!normalized) {
      return mapping.fallbackValue && mapping.fallbackValue.length > 0
        ? (normalizeCssColor(mapping.fallbackValue) ?? null)
        : null;
    }
    return normalized;
  }

  return stringValue;
}

/**
 * Convenience helper: given the mappings for a single element and a target
 * property, returns the resolved value or null if no mapping targets that
 * property.
 */
export function resolveElementPropertyValue(
  mappings: EnhancedFieldMapping[] | undefined,
  property: string,
  brandData: Record<string, unknown> | null
): string | null {
  if (!mappings || mappings.length === 0) return null;
  const match = mappings.find((m) => m.targetProperty === property);
  if (!match) return null;
  return resolveMappedValueFromBrandData(match, brandData);
}
